import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DetailProjectService } from 'src/app/services/detail-project.service';
import { HubspotService } from 'src/app/services/hubspot.service';
import { ProfileService } from 'src/app/services/profile.service';
import { ExtraAddress, Profile } from '../../../../models/register.models';
import { ModalService } from '../../../modal/modal.service';

@Component({
  selector: 'fun-overview-priority-group-detail',
  templateUrl: './overview-priority-group-detail.component.html',
  styleUrls: ['./overview-priority-group-detail.component.scss'],
})
export class OverviewPriorityGroupDetailComponent implements OnInit {

  @Input() public stage: any;
  @Input() public detailProject: any;
  @Input() public projectDeal: any;
  @Output() public removeComponentEvent = new EventEmitter<any>();
  @Output() public addressChangedEvent = new EventEmitter<void>();

  // Vista actual: 'detail' | 'vinculate_loading' | 'vinculate_success' | 'vinculate_error' | 'addresses' | 'address_form'
  currentView: string = 'detail';

  hasVinculation: boolean = false;
  highestUserPriority: number = null;
  expandedGroups: { [key: number]: boolean } = {};

  // Vinculación
  vinculatingGroup: any = null;

  // Direcciones
  profile: Profile;
  addresses: ExtraAddress[] = [];
  addressesLoading: boolean = false;

  // Formulario de dirección
  editingAddress: ExtraAddress | null = null;
  isEditAddress: boolean = false;

  constructor(
    public chdRef: ChangeDetectorRef,
    private modalService: ModalService,
    private storage: Storage,
    private hubspotService: HubspotService,
    private profileService: ProfileService,
    private detailProjectService: DetailProjectService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    //console.log(this.stage, 'this.stage')
    this.hasVinculation = this.stage.priority_groups.some((pg: any) => pg.isEnabledForUser);
    this.highestUserPriority = this.stage?.highestUserPriority ?? null;
    this.chdRef.detectChanges();
  }

  // ============================================
  // DETAIL VIEW - Timeline de grupos prioritarios
  // ============================================

  get groupedPriorityGroups(): { priority: number, items: any[] }[] {
    if (!this.stage?.priority_groups) return [];

    const sorted = this.stage.priority_groups.slice().sort((a: any, b: any) => a.priority - b.priority);

    const groups: { priority: number, items: any[] }[] = [];
    sorted.forEach((pg: any) => {
      let group = groups.find(g => g.priority === pg.priority);
      if (!group) {
        group = { priority: pg.priority, items: [] };
        groups.push(group);
      }
      group.items.push(pg);
    });

    return groups;
  }

  private readonly provinciaAComunidad: { [key: string]: string } = {
    '01': 'País Vasco', '20': 'País Vasco', '48': 'País Vasco',
    '02': 'Castilla-La Mancha', '13': 'Castilla-La Mancha', '16': 'Castilla-La Mancha', '19': 'Castilla-La Mancha', '45': 'Castilla-La Mancha',
    '03': 'Comunitat Valenciana', '12': 'Comunitat Valenciana', '46': 'Comunitat Valenciana',
    '04': 'Andalucía', '11': 'Andalucía', '14': 'Andalucía', '18': 'Andalucía', '21': 'Andalucía', '23': 'Andalucía', '29': 'Andalucía', '41': 'Andalucía',
    '05': 'Castilla y León', '09': 'Castilla y León', '24': 'Castilla y León', '34': 'Castilla y León', '37': 'Castilla y León', '40': 'Castilla y León', '42': 'Castilla y León', '47': 'Castilla y León', '49': 'Castilla y León',
    '06': 'Extremadura', '10': 'Extremadura',
    '07': 'Illes Balears',
    '08': 'Catalunya', '17': 'Catalunya', '25': 'Catalunya', '43': 'Catalunya',
    '15': 'Galicia', '27': 'Galicia', '32': 'Galicia', '36': 'Galicia',
    '22': 'Aragón', '44': 'Aragón', '50': 'Aragón',
    '26': 'La Rioja',
    '28': 'Comunidad de Madrid',
    '30': 'Región de Murcia',
    '31': 'Navarra',
    '33': 'Asturias',
    '35': 'Canarias', '38': 'Canarias',
    '39': 'Cantabria',
    '51': 'Ceuta',
    '52': 'Melilla',
  };

  parseValues(value: string): string[] {
    if (!value) return [];
    return value.split(',').map(v => v.trim()).filter(v => v !== '');
  }

  groupByComunidad(value: string): { comunidad: string, codes: string[] }[] {
    const codes = this.parseValues(value);
    const map: { [key: string]: string[] } = {};
    codes.forEach(cp => {
      const provincia = cp.toString().padStart(5, '0').slice(0, 2);
      const comunidad = this.provinciaAComunidad[provincia] ?? 'Otras';
      if (!map[comunidad]) map[comunidad] = [];
      map[comunidad].push(cp);
    });
    return Object.entries(map).map(([comunidad, codes]) => ({ comunidad, codes }));
  }

  toggleExpand(pgId: number, event?: Event): void {
    if (event) event.stopPropagation();
    this.expandedGroups[pgId] = !this.expandedGroups[pgId];
    this.chdRef.detectChanges();
  }

  isExpanded(pgId: number): boolean {
    return !!this.expandedGroups[pgId];
  }

  public hasUserPriorityGroup(stage: any): boolean {
    return this.detailProjectService.hasUserPriorityGroup(stage);
  }

  // ============================================
  // VINCULACIÓN
  // ============================================

  sendVinculation(group: any): void {
    this.vinculatingGroup = group;
    this.currentView = 'vinculate_loading';
    this.chdRef.detectChanges();

    this.storage.get('USERNAME').then(username => {
      const body = this.translate.instant('PRIORITY_GROUP.EMAIL.BODY');
      const subject = this.translate.instant('PRIORITY_GROUP.EMAIL.SUBJECT', { username, groupName: group.name });

      this.hubspotService.createTask(subject, body).subscribe(
        () => {
          this.currentView = 'vinculate_success';
          this.chdRef.detectChanges();
        },
        error => {
          console.log(error, 'error');
          this.currentView = 'vinculate_error';
          this.chdRef.detectChanges();
        }
      );
    });
  }

  retryVinculation(): void {
    if (this.vinculatingGroup) {
      this.sendVinculation(this.vinculatingGroup);
    }
  }

  // ============================================
  // DIRECCIONES
  // ============================================

  goToAddresses(): void {
    this.currentView = 'addresses';
    this.loadProfile();
    this.chdRef.detectChanges();
  }

  loadProfile(): void {
    this.addressesLoading = true;
    this.storage.get('USERNAME').then(val => {
      if (!val) {
        this.addressesLoading = false;
        return;
      }
      this.profileService.getProfile(val).subscribe(
        (resp: Profile) => {
          this.profile = resp;
          this.addresses = resp.addresses || [];
          this.addressesLoading = false;
          this.chdRef.detectChanges();
        },
        () => {
          this.addressesLoading = false;
          this.chdRef.detectChanges();
        }
      );
    });
  }

  // Evento desde fun-profile-addresses en modo embebido
  onAddAddressFromList(): void {
    this.editingAddress = null;
    this.isEditAddress = false;
    this.currentView = 'address_form';
    this.chdRef.detectChanges();
  }

  // Evento desde fun-profile-addresses en modo embebido
  onEditAddressFromList(address: ExtraAddress): void {
    this.editingAddress = address;
    this.isEditAddress = true;
    this.currentView = 'address_form';
    this.chdRef.detectChanges();
  }

  // Evento desde fun-profile-addresses cuando se guarda
  onAddressSaved(addresses: ExtraAddress[]): void {
    this.loadProfile();
    this.updateHubspotToValidation();
    this.addressChangedEvent.emit();
    this.chdRef.detectChanges();
  }

  private updateHubspotToValidation(): void {
    if (this.projectDeal?.id) {
      if (this.canMoveToValidation(this.projectDeal.properties?.dealstage)) {
        this.hubspotService.updateDeal(this.projectDeal.id, 'validation', {}).subscribe();
      }
      return;
    }
    this.storage.get('USERNAME').then((email: string) => {
      if (!email || !this.detailProject?.id) return;
      this.hubspotService.getDeals(email, this.detailProject.id).subscribe((res: any) => {
        if (res.success && res.data?.results?.length) {
          const deal = res.data.results[0];
          if (this.canMoveToValidation(deal.properties?.dealstage)) {
            this.hubspotService.updateDeal(deal.id, 'validation', {}).subscribe();
          }
        }
      });
    });
  }

  private canMoveToValidation(currentStageId: string): boolean {
    if (!currentStageId) return true;
    const blocked = ['ok', 'reservation', 'sign', 'payment', 'preinvestment', 'investmentIntention'];
    return !blocked.some(key => currentStageId === this.hubspotService.getStage(key));
  }

  // ============================================
  // FORMULARIO DE DIRECCIÓN (eventos desde fun-profile-address-form)
  // ============================================

  onSaveAddress(event: { formData: any, selectedTypes: number[] }): void {
    const { formData, selectedTypes } = event;
    const requests = [];
    let editedAddressUsed = false;

    for (let i = 0; i < selectedTypes.length; i++) {
      const type = selectedTypes[i];
      const existing = this.addresses.find(a =>
        a.type === type && !(this.isEditAddress && this.editingAddress && a.id === this.editingAddress.id)
      );

      if (existing && existing.id) {
        const model = { ...formData, id: existing.id, type: type };
        requests.push(this.profileService.updateExtraAddres(model));
      } else if (this.isEditAddress && this.editingAddress && this.editingAddress.id && !editedAddressUsed) {
        const model = { ...formData, id: this.editingAddress.id, type: type };
        requests.push(this.profileService.updateExtraAddres(model));
        editedAddressUsed = true;
      } else {
        const model = { ...formData, type: type };
        requests.push(this.profileService.createExtraAddres(model));
      }
    }

    if (requests.length === 0) {
      return;
    }

    forkJoin(requests).subscribe(
      () => {
        this.currentView = 'addresses';
        this.loadProfile();
        this.updateHubspotToValidation();
        this.addressChangedEvent.emit();
        this.chdRef.detectChanges();
      },
      () => {
        this.chdRef.detectChanges();
      }
    );
  }

  onCancelAddressForm(): void {
    this.currentView = 'addresses';
    this.chdRef.detectChanges();
  }

  // ============================================
  // NAVEGACIÓN
  // ============================================

  goBack(): void {
    if (this.currentView === 'vinculate_loading' || this.currentView === 'vinculate_success' || this.currentView === 'vinculate_error') {
      this.currentView = 'detail';
      this.vinculatingGroup = null;
    } else if (this.currentView === 'addresses') {
      this.currentView = 'detail';
    } else if (this.currentView === 'address_form') {
      this.currentView = 'addresses';
    } else {
      this.removeComponentEvent.emit();
    }
    this.chdRef.detectChanges();
  }

  closeModal(): void {
    this.modalService.removeModal();
  }
}
